import axios from 'axios';
import { apiClient } from '@/lib/api-client';
import { logger } from '@/lib/logger';
import {
  Condition,
  ConditionFormData,
  ConditionBulkOperation,
  ConditionListParams,
  ConditionTestRequest,
  ConditionTestResponse,
  Protocol,
  ProtocolFormData,
  ProtocolListParams,
  AddProtocolItemRequest,
  Template,
  TemplateFormData,
  TemplateListParams,
  AddProtocolToTemplateRequest,
  RemoveProtocolFromTemplateRequest,
  TemplateTestRequest,
  TemplateTestResponse,
  Execution,
  ExecutionListParams,
  EvaluateRequest,
  EvaluateResponse,
  PreviewRequest,
  PreviewResponse,
  ProtocolAssignment,
  AssignmentFormData,
  AssignmentListParams,
  ClientSuperClinicCode,
  ApplyProtocolsRequest,
  ApplyProtocolsResponse,
  ProtocolAuditEntry,
  EvaluationHistory,
  RecommendationAnalytics,
} from '@/types/recommendations';
import { fetchAccessToken } from '@/utils/auth';

const API_BASE = 'ai/recommendations';

const logApplyProtocolsOrganizationWarning = (clientId: string, error: unknown) => {
  if (axios.isAxiosError(error)) {
    logger.warn('Could not fetch user organization for applyProtocols; proceeding without it', {
      clientId,
      httpStatus: error.response?.status,
      message: error.message,
    });
    return;
  }
  logger.warn('Could not fetch user organization for applyProtocols; proceeding without it', {
    clientId,
    message: error instanceof Error ? error.message : String(error),
  });
};

const safeApiErrorDetail = (data: unknown): string | undefined => {
  if (data == null) return undefined;
  if (typeof data === 'string') return data.length > 300 ? `${data.slice(0, 300)}…` : data;
  if (typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    const piece = o.detail ?? o.message ?? o.error;
    if (typeof piece === 'string') return piece.length > 300 ? `${piece.slice(0, 300)}…` : piece;
  }
  return undefined;
};

const buildApplyProtocolsErrorMessage = (
  kind: 'add' | 'remove',
  context: {
    clientId: string;
    executionId?: string | undefined;
    protocolId: string | undefined;
  },
  error: unknown
): string => {
  const base =
    kind === 'add'
      ? 'Failed to apply protocol. Please try again or contact support.'
      : 'Failed to remove protocol. Please try again or contact support.';

  if (axios.isAxiosError(error)) {
    const headers = error.response?.headers ?? {};
    const ridRaw =
      headers['x-request-id'] ??
      headers['X-Request-Id'] ??
      headers['x-amzn-requestid'] ??
      headers['x-correlation-id'];
    const requestId =
      ridRaw !== undefined && ridRaw !== null
        ? String(Array.isArray(ridRaw) ? ridRaw[0] : ridRaw)
        : '';

    logger.error(
      kind === 'add' ? 'Protocol assignment apply failed' : 'Protocol assignment removal failed',
      {
        ...context,
        httpStatus: error.response?.status,
        statusText: error.response?.statusText,
        axiosCode: error.code,
        axiosMessage: error.message,
        apiDetail: safeApiErrorDetail(error.response?.data),
        requestId: requestId || undefined,
      }
    );

    const hints: string[] = [];
    if (error.response?.status != null) hints.push(`HTTP ${error.response.status}`);
    if (requestId.length > 0 && requestId.length <= 80) hints.push(`req ${requestId}`);
    const suffix = hints.length ? ` (${hints.join(' · ')})` : '';

    return `${base}${suffix}`;
  }

  logger.error(
    kind === 'add' ? 'Protocol assignment apply failed' : 'Protocol assignment removal failed',
    {
      ...context,
      message: error instanceof Error ? error.message : String(error),
    }
  );

  return base;
};

// Paginated response type
interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  page_size: number;
}

// Handle both array and paginated responses
const handleListResponse = <T>(response: any): PaginatedResponse<T> => {
  if (Array.isArray(response.data)) {
    return {
      data: response.data,
      total: response.data.length,
      page: 1,
      page_size: response.data.length,
    };
  }
  return {
    data: response.data.results || response.data.data || response.data.items || [],
    total: response.data.count || response.data.total || 0,
    page: response.data.page || 1,
    page_size: response.data.page_size || 20,
  };
};

export const recommendationsApi = {
  // ============================================
  // CONDITIONS (replaces Rules)
  // ============================================

  getConditions: async (params?: ConditionListParams): Promise<PaginatedResponse<Condition>> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/conditions`, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return handleListResponse<Condition>(response);
  },

  getAllConditions: async (params?: ConditionListParams): Promise<Condition[]> => {
    const pageSize = params?.page_size ?? 500;
    let page = 1;
    let allConditions: Condition[] = [];
    let total = Number.POSITIVE_INFINITY;

    while (allConditions.length < total) {
      const response = await recommendationsApi.getConditions({
        ...params,
        page_size: pageSize,
        page,
      });

      allConditions = [...allConditions, ...response.data];
      total = response.total > 0 ? response.total : allConditions.length;

      if (response.data.length === 0 || response.data.length < pageSize) {
        break;
      }

      page += 1;
    }

    return allConditions;
  },

  getConditionById: async (id: string): Promise<Condition> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/conditions/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  createCondition: async (data: ConditionFormData): Promise<Condition> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/conditions`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  updateCondition: async (id: string, data: Partial<ConditionFormData>): Promise<Condition> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.put(`${API_BASE}/conditions/${id}`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  deleteCondition: async (id: string): Promise<void> => {
    const accessToken = await fetchAccessToken();
    await axios.delete(`${API_BASE}/conditions/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  },

  bulkConditionOperation: async (data: ConditionBulkOperation): Promise<void> => {
    const accessToken = await fetchAccessToken();
    await axios.post(`${API_BASE}/conditions/bulk`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  },

  testCondition: async (
    conditionId: string,
    data: ConditionTestRequest
  ): Promise<ConditionTestResponse> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/conditions/${conditionId}/test`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // ============================================
  // PROTOCOLS (replaces ClinicalTemplate)
  // ============================================

  getProtocols: async (params?: ProtocolListParams): Promise<PaginatedResponse<Protocol>> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/protocols`, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return handleListResponse<Protocol>(response);
  },

  getAllProtocols: async (params?: ProtocolListParams): Promise<Protocol[]> => {
    const pageSize = params?.page_size ?? 500;
    let page = 1;
    let allProtocols: Protocol[] = [];
    let total = Number.POSITIVE_INFINITY;

    while (allProtocols.length < total) {
      const response = await recommendationsApi.getProtocols({
        ...params,
        page_size: pageSize,
        page,
      });

      allProtocols = [...allProtocols, ...response.data];
      total = response.total > 0 ? response.total : allProtocols.length;

      if (response.data.length === 0 || response.data.length < pageSize) {
        break;
      }

      page += 1;
    }

    return allProtocols;
  },

  getProtocolById: async (id: string): Promise<Protocol> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/protocols/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  createProtocol: async (data: ProtocolFormData): Promise<Protocol> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/protocols`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  updateProtocol: async (id: string, data: Partial<ProtocolFormData>): Promise<Protocol> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.put(`${API_BASE}/protocols/${id}`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  deleteProtocol: async (id: string): Promise<void> => {
    const accessToken = await fetchAccessToken();
    await axios.delete(`${API_BASE}/protocols/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  },

  addProtocolItem: async (protocolId: string, data: AddProtocolItemRequest): Promise<Protocol> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/protocols/${protocolId}/add_item`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // ============================================
  // TEMPLATES (NEW entity - links Condition to Protocol)
  // ============================================

  getTemplates: async (params?: TemplateListParams): Promise<PaginatedResponse<Template>> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/templates`, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return handleListResponse<Template>(response);
  },

  getTemplateById: async (id: string, params?: { expand?: string }): Promise<Template> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/templates/${id}`, {
      params: params?.expand ? { expand: params.expand } : undefined,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  createTemplate: async (data: TemplateFormData): Promise<Template> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/templates`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  updateTemplate: async (id: string, data: Partial<TemplateFormData>): Promise<Template> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.put(`${API_BASE}/templates/${id}`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  deleteTemplate: async (id: string): Promise<void> => {
    const accessToken = await fetchAccessToken();
    await axios.delete(`${API_BASE}/templates/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  },

  addProtocolToTemplate: async (
    templateId: string,
    data: AddProtocolToTemplateRequest
  ): Promise<Template> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/templates/${templateId}/add_protocol`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  removeProtocolFromTemplate: async (
    templateId: string,
    data: RemoveProtocolFromTemplateRequest
  ): Promise<Template> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/templates/${templateId}/remove_protocol`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  testTemplate: async (
    templateId: string,
    data: TemplateTestRequest
  ): Promise<TemplateTestResponse> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/templates/${templateId}/test`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // ============================================
  // ENGINE (Evaluation)
  // ============================================

  evaluate: async (data: EvaluateRequest): Promise<EvaluateResponse> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/engine/evaluate`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  preview: async (data: PreviewRequest): Promise<PreviewResponse> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/engine/preview`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  substituteCombination: async (
    nutraceuticalIds: string[],
    therapyIds?: string[]
  ): Promise<{ substitutes?: unknown }> => {
    const accessToken = await fetchAccessToken();
    const body: Record<string, unknown> = { nutraceutical_ids: nutraceuticalIds };
    if (therapyIds && therapyIds.length > 0) {
      body.therapy_ids = therapyIds;
    }
    const response = await axios.post(`${API_BASE}/engine/substitute_combination`, body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  },

  substituteIv: async (
    nutraceuticalIds: string[],
    therapyIds?: string[]
  ): Promise<{ substitutes?: unknown }> => {
    const accessToken = await fetchAccessToken();
    const body: Record<string, unknown> = { nutraceutical_ids: nutraceuticalIds };
    if (therapyIds && therapyIds.length > 0) {
      body.therapy_ids = therapyIds;
    }
    const response = await axios.post(`${API_BASE}/engine/substitute_iv`, body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  },

  substituteCombinationAndIv: async (
    nutraceuticalIds: string[],
    therapyIds?: string[]
  ): Promise<{ substitutes?: unknown }> => {
    const accessToken = await fetchAccessToken();
    const body: Record<string, unknown> = { nutraceutical_ids: nutraceuticalIds };
    if (therapyIds && therapyIds.length > 0) {
      body.therapy_ids = therapyIds;
    }
    const response = await axios.post(`${API_BASE}/engine/substitute_combination_and_iv`, body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  },

  // ============================================
  // EXECUTIONS
  // ============================================

  getExecutions: async (params?: ExecutionListParams): Promise<PaginatedResponse<Execution>> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/executions`, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return handleListResponse<Execution>(response);
  },

  getExecutionById: async (id: string): Promise<Execution> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/executions/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // ============================================
  // ASSIGNMENTS (replaces UserProtocolAssignment)
  // ============================================

  getAssignments: async (
    params?: AssignmentListParams
  ): Promise<PaginatedResponse<ProtocolAssignment>> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/assignments`, {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return handleListResponse<ProtocolAssignment>(response);
  },

  getAssignmentById: async (id: string): Promise<ProtocolAssignment> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/assignments/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  createAssignment: async (data: AssignmentFormData): Promise<ProtocolAssignment> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(`${API_BASE}/assignments`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  activateAssignment: async (id: string): Promise<ProtocolAssignment> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(
      `${API_BASE}/assignments/${id}/activate`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response.data;
  },

  completeAssignment: async (id: string): Promise<ProtocolAssignment> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(
      `${API_BASE}/assignments/${id}/complete`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response.data;
  },

  cancelAssignment: async (id: string): Promise<ProtocolAssignment> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(
      `${API_BASE}/assignments/${id}/cancel`,
      {},
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response.data;
  },

  // ============================================
  // CLIENT-SPECIFIC OPERATIONS
  // ============================================

  // SuperClinic Codes Management
  getClientCodes: async (clientId: string): Promise<ClientSuperClinicCode[]> => {
    const accessToken = await fetchAccessToken();
    const response = await apiClient.get(`/users/users/${clientId}/superclinic-codes`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  addClientCode: async (
    clientId: string,
    data: { code: string; description: string; category: string }
  ): Promise<ClientSuperClinicCode> => {
    const accessToken = await fetchAccessToken();
    const response = await apiClient.post(`/users/users/${clientId}/superclinic-codes`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  updateCodeStatus: async (
    clientId: string,
    codeId: string,
    isActive: boolean
  ): Promise<ClientSuperClinicCode> => {
    const accessToken = await fetchAccessToken();
    const response = await apiClient.patch(
      `/users/users/${clientId}/superclinic-codes/${codeId}`,
      { isActive },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return response.data;
  },

  removeClientCode: async (clientId: string, codeId: string): Promise<void> => {
    const accessToken = await fetchAccessToken();
    await apiClient.delete(`/users/users/${clientId}/superclinic-codes/${codeId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  },

  bulkUpdateCodes: async (
    clientId: string,
    data: { codeIds: string[]; isActive: boolean }
  ): Promise<ClientSuperClinicCode[]> => {
    const accessToken = await fetchAccessToken();
    const response = await apiClient.post(
      `/users/users/${clientId}/superclinic-codes/bulk-update`,
      data,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response.data;
  },

  // Evaluate client protocols (signs/symptoms match consultation payload format)
  evaluateClientProtocols: async (
    clientId: string,
    codes?: string[],
    signs?: Array<{ name: string; sign_id: string; superclinic_code?: string }>,
    symptoms?: Array<{ name: string; symptom_id: string; superclinic_code?: string }>,
    diagnoses?: Array<{ name: string; diagnosis_id: string; superclinic_code?: string }>
  ): Promise<EvaluateResponse> => {
    const accessToken = await fetchAccessToken();

    // If no codes provided, fetch active client codes
    let evaluationCodes = codes;
    if (!evaluationCodes || evaluationCodes.length === 0) {
      const clientCodes = await recommendationsApi.getClientCodes(clientId);
      evaluationCodes = clientCodes.filter((c) => c.isActive).map((c) => c.code);
    }

    const body: EvaluateRequest = {
      patient_id: clientId,
      superclinic_codes: evaluationCodes,
      dry_run: true, // Always dry run for client evaluations
    };
    if (signs?.length) {
      body.signs = signs.map((s) => ({
        name: s.name,
        sign_id: s.sign_id,
        ...(s.superclinic_code && { superclinic_code: s.superclinic_code }),
      }));
    }
    if (symptoms?.length) {
      body.symptoms = symptoms.map((s) => ({
        name: s.name,
        symptom_id: s.symptom_id,
        ...(s.superclinic_code && { superclinic_code: s.superclinic_code }),
      }));
    }
    if (diagnoses?.length) {
      body.diagnoses = diagnoses.map((d) => ({
        name: d.name,
        diagnosis_id: d.diagnosis_id,
        ...(d.superclinic_code && { superclinic_code: d.superclinic_code }),
      }));
    }

    const response = await axios.post(`${API_BASE}/engine/evaluate`, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  },

  // Apply protocols to client
  applyProtocols: async (data: ApplyProtocolsRequest): Promise<ApplyProtocolsResponse> => {
    const accessToken = await fetchAccessToken();

    // Fetch the user's organization to include in the request
    let organizationId: string | undefined;
    try {
      const userResponse = await apiClient.get(`/users/users/${data.clientId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      organizationId = userResponse.data?.organization?.id || userResponse.data?.organization;
    } catch (error: unknown) {
      logApplyProtocolsOrganizationWarning(data.clientId, error);
    }

    // Create assignments for each protocol addition
    let appliedAdditions = 0;
    let appliedRemovals = 0;
    const errors: string[] = [];

    for (const addition of data.additions || []) {
      try {
        await axios.post(
          `${API_BASE}/assignments`,
          {
            user: data.clientId,
            protocol: addition.protocolId,
            source: 'engine', // New source value
            source_execution: data.executionId,
            doctor_notes: addition.reason,
            metadata: {
              notes: addition.notes,
            },
          },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        appliedAdditions++;
      } catch (error: unknown) {
        errors.push(
          buildApplyProtocolsErrorMessage(
            'add',
            {
              clientId: data.clientId,
              executionId: data.executionId,
              protocolId: addition.protocolId,
            },
            error
          )
        );
      }
    }

    // Handle removals by canceling assignments
    for (const removal of data.removals || []) {
      try {
        // Find the assignment for this protocol
        const assignmentsResponse = await axios.get(`${API_BASE}/assignments`, {
          params: {
            user_id: data.clientId,
            protocol_id: removal.protocolId,
            status: 'active',
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const assignments = assignmentsResponse.data.results || assignmentsResponse.data || [];
        for (const assignment of assignments) {
          await axios.post(
            `${API_BASE}/assignments/${assignment.assignment_id}/cancel`,
            {},
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
        }
        appliedRemovals++;
      } catch (error: unknown) {
        errors.push(
          buildApplyProtocolsErrorMessage(
            'remove',
            {
              clientId: data.clientId,
              executionId: data.executionId,
              protocolId: removal.protocolId,
            },
            error
          )
        );
      }
    }

    return {
      success: errors.length === 0,
      appliedAdditions,
      appliedRemovals,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  // Audit Trail
  getProtocolHistory: async (clientId: string): Promise<ProtocolAuditEntry[]> => {
    const accessToken = await fetchAccessToken();
    const response = await apiClient.get(`/users/users/${clientId}/protocols/history`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  getEvaluationHistory: async (clientId: string): Promise<EvaluationHistory[]> => {
    const accessToken = await fetchAccessToken();
    const response = await apiClient.get(`/users/users/${clientId}/evaluations/history`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // ============================================
  // ANALYTICS
  // ============================================

  getAnalytics: async (): Promise<RecommendationAnalytics> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/analytics`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  getConditionUsageStats: async (startDate?: Date, endDate?: Date): Promise<unknown[]> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/analytics/condition-usage`, {
      params: { startDate, endDate },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  getPerformanceMetrics: async (): Promise<{
    executionTimeTrend: Array<{ date: string; avgTime: number }>;
    successRateTrend: Array<{ date: string; successRate: number }>;
  }> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/analytics/performance`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return response.data;
  },

  // ============================================
  // IMPORT/EXPORT
  // ============================================

  exportConditions: async (conditionIds?: string[]): Promise<Blob> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.post(
      `${API_BASE}/conditions/export`,
      { condition_ids: conditionIds },
      {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response.data;
  },

  importConditions: async (
    file: File
  ): Promise<{ imported: number; failed: number; errors: string[] }> => {
    const accessToken = await fetchAccessToken();
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_BASE}/conditions/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  },

  // ============================================
  // UTILITY ENDPOINTS
  // ============================================

  searchSuperclinicCodes: async (
    query: string
  ): Promise<Array<{ code: string; description: string; category: string }>> => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get('references/catalog/registry', {
      params: { search: query, is_active: true, limit: 50 },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const data = Array.isArray(response.data) ? response.data : response.data.items || [];
    return data.map((item: unknown) => {
      const typedItem = item as {
        code: string;
        description?: string;
        name?: string;
        category?: string;
      };
      return {
        code: typedItem.code,
        description: typedItem.description || typedItem.name || '',
        category: typedItem.category || 'other',
      };
    });
  },

  searchProtocols: async (
    query: string
  ): Promise<
    Array<{
      id: string;
      name: string;
      description: string;
      category: string;
      type: string;
    }>
  > => {
    const accessToken = await fetchAccessToken();
    const response = await axios.get(`${API_BASE}/protocols`, {
      params: { search: query, status: 'ACTIVE', page_size: 50 },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const data = response.data.results || response.data.data || response.data || [];
    return data.map((item: Protocol) => ({
      id: item.protocol_id,
      name: item.name,
      description: item.description,
      category: item.category,
      type: 'protocol',
    }));
  },
};
